import React, { useState, useEffect } from 'react';
import { FaEnvelope, FaMapMarkerAlt } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { API_BASE } from '../config';
import { useLanguage } from '../context/LanguageContext';

const About = () => {
  const [about, setAbout] = useState(null);
  const [experience, setExperience] = useState([]);
  const [education, setEducation] = useState([]);
  const [skills, setSkills] = useState([]);
  const { t } = useLanguage();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/portfolio`);
        const data = await res.json();
        setAbout(data.about || {});
        setExperience(data.experience || []);
        setEducation(data.education || []);
        setSkills(data.skills || []);
      } catch (error) {
        console.error('Error fetching data:', error);
        setAbout({
          name: 'Bernard Banda',
          title: t('about.fallbackTitle') || 'Computer Science Student',
          bio: t('about.bioFallback') || 'I am a passionate computer science student dedicated to building AI-powered web-based and software systems. I have a strong foundation in web development, software engineering, database design, database management and data analysis (Beginner). My journey in technology began with a curiosity for how things work, and it has evolved into a passion for creating impactful applications.',
          email: 'bandabernard917@gmail.com',
          location: t('about.fallbackLocation') || 'Lusaka'
        });
        setExperience([
          { id: 1, company: 'Africa University', position: 'Full Stack Developer', start_date: '2026-01-19', end_date: '2025-04-22', description: 'Developed a modular, enterprise-grade web application for Africa University\'s Fleet and Facilities Unit (FFU).', location: 'On site' },
          { id: 2, company: 'Africa University', position: 'Backend Developer', start_date: '2025-01-13', end_date: '2025-04-27', description: 'Developed an health care system for Africa university as a group project.', location: 'Lusaka, Zambia' }
        ]);
        setEducation([
          { id: 1, institution: 'Africa University', degree: 'Bachelor of Science Honours in Computer Science', field_of_study: 'Computer Science', start_date: '2023-08-13', gpa: '3.5' },
          { id: 2, institution: 'Zambia Institute of Mass Communications (ZAMCOM)', certificate: 'Trades Certificate', field_of_study: 'Introduction to ICT and Graphic design', start_date: '2022-01-10', end_date: '2022-06-10', result: 'Pass' }
        ]);
        setSkills([
          { id: 1, name: 'JavaScript', category: 'languages', proficiency_level: 90 },
          { id: 2, name: 'Python', category: 'languages', proficiency_level: 85 },
          { id: 4, name: 'React', category: 'frontend', proficiency_level: 95 },
          { id: 5, name: 'HTML/CSS', category: 'frontend', proficiency_level: 95 },
          { id: 6, name: 'Node.js', category: 'backend', proficiency_level: 85 },
          { id: 7, name: 'PostgreSQL', category: 'backend', proficiency_level: 80 },
          { id: 8, name: 'Git & GitHub', category: 'tools', proficiency_level: 90 },
          { id: 9, name: 'Docker', category: 'tools', proficiency_level: 75 },
        ]);
      }
    };

    fetchData();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return t('about.present');
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  };

  const skillsByCategory = skills.reduce((acc, skill) => {
    if (!acc[skill.category]) {
      acc[skill.category] = [];
    }
    acc[skill.category].push(skill);
    return acc;
  }, {});

  const getCategoryTitle = (category) => {
    const map = {
      languages: t('about.languages'),
      frontend: t('about.frontend'),
      backend: t('about.backend'),
      tools: t('about.tools'),
    };
    return map[category] || category;
  };

  const TimelineItem = ({ item, index, type }) => {
    const isEven = index % 2 === 0;
    const title = type === 'experience' ? item.position : item.degree || item.certificate;
    const subtitle = type === 'experience' ? item.company : item.institution;
    const start = formatDate(item.start_date);
    const end = item.is_current ? t('about.present') : formatDate(item.end_date);
    const meta = `${start} - ${end}`;
    const extra = type === 'experience'
      ? (item.location ? ` • ${item.location}` : '')
      : (item.gpa ? ` • GPA: ${item.gpa}` : '');
    const field = item.field_of_study || item.description;

    return (
      <motion.div
        className={`timeline-item ${isEven ? 'timeline-left' : 'timeline-right'}`}
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: '-50px' }}
        transition={{ duration: 0.5, delay: index * 0.15 }}
      >
        <div className="timeline-dot" />
        <div className="timeline-content">
          <div className="timeline-date">{meta}</div>
          <h3 className="timeline-title">{title}</h3>
          <h4 className="timeline-subtitle">
            {subtitle}{extra}
          </h4>
          {field && <p className="timeline-desc">{field}</p>}
        </div>
      </motion.div>
    );
  };

  return (
    <div className="about-page">
      <section className="section" style={{ paddingTop: '8rem', background: 'var(--bg-light)' }}>
        <h2 className="section-title">{t('about.title')}</h2>

        <div className="about-content" data-tour="about-content">
          <div className="about-image fade-in-up">
            <img
              src={about?.profile_image || '/images/profile/Myimage.jpeg'}
              alt="Profile"
            />
          </div>

          <div className="about-text fade-in-up stagger-2">
            <h3>{about?.title || 'Computer Science Student'}</h3>
            <p>{about?.bio || 'Loading...'}</p>

            <div style={{ marginTop: '2rem' }}>
              <div className="contact-item" style={{ marginBottom: '1rem' }}>
                <div className="contact-icon" style={{ width: '40px', height: '40px', fontSize: '1rem' }}>
                  <FaEnvelope />
                </div>
                <div className="contact-text">
                  <h4>{t('contact.email')}</h4>
                  <p style={{ color: '#000' }}>{about?.email || 'bandabernard917@gmail.com'}</p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon" style={{ width: '40px', height: '40px', fontSize: '1rem' }}>
                  <FaMapMarkerAlt />
                </div>
                <div className="contact-text">
                  <h4>{t('contact.location')}</h4>
                  <p style={{ color: '#000' }}>{about?.location || 'Lusaka, Zambia'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section" style={{ background: '#fff' }}>
        <h2 className="section-title">{t('about.skillsTech')}</h2>

        {Object.entries(skillsByCategory).map(([category, categorySkills]) => (
          <div key={category} style={{ marginBottom: '3rem' }}>
            <h3 style={{ textAlign: 'center', marginBottom: '2rem', color: 'var(--primary)', fontSize: '1.5rem', textTransform: 'capitalize' }}>
              {getCategoryTitle(category)}
            </h3>
            <div className="skills-grid">
              {categorySkills.map((skill) => (
                <div key={skill.id} className="skill-card">
                  <div className="skill-info">
                    <span className="skill-name">{skill.name}</span>
                    {skill.proficiency_level && (
                      <span className="skill-level">{skill.proficiency_level}%</span>
                    )}
                  </div>
                  {skill.proficiency_level && (
                    <div className="skill-bar">
                      <div
                        className="skill-progress"
                        style={{ width: `${skill.proficiency_level}%` }}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>

      {experience.length > 0 && (
        <section className="section" style={{ background: 'var(--bg-light)' }}>
          <h2 className="section-title">{t('about.experience')}</h2>
          <div className="timeline">
            {experience.map((exp, index) => (
              <TimelineItem key={exp.id} item={exp} index={index} type="experience" />
            ))}
          </div>
        </section>
      )}

      {education.length > 0 && (
        <section className="section" style={{ background: '#fff' }}>
          <h2 className="section-title">{t('about.education')}</h2>
          <div className="timeline">
            {education.map((edu, index) => (
              <TimelineItem key={edu.id} item={edu} index={index} type="education" />
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default About;
